// Lightweight client for ESPN NFL scoreboard to get in-progress games

export interface LiveGame {
  id: string;
  homeTeamName: string;
  homeAbbr: string;
  homeScore: number;
  awayTeamName: string;
  awayAbbr: string;
  awayScore: number;
  period: number;
  clock: string;
  statusText: string;
  statusState?: string; // 'pre' | 'in' | 'post' (ESPN states)
  // Situation details (when available during live games)
  possessionAbbr?: string; // e.g., 'GB' for the team with the ball
  downDistanceText?: string; // e.g., '4th & 2 at GB 30'
  shortDownDistanceText?: string; // e.g., '4th & 2'
  possessionText?: string; // e.g., 'GB 30'
  isRedZone?: boolean;
  yardLine?: number; // 0-100 yards progressed toward opponent end zone
  // Derived helpers for "between plays" transitional states
  neutralPhase?: boolean; // e.g., after score/change of possession when ESPN sets down = -1
  lastPlayText?: string;
  lastDriveSummary?: string; // e.g., '4 plays, 80 yards, 1:45'
  driveResult?: string; // e.g., 'TD', 'FG', 'PUNT'
  scoringTeamAbbr?: string; // abbr for team credited with last play
  transitionNote?: string; // e.g., 'Kickoff pending', 'Change of possession'
  nextReceivingAbbr?: string; // who likely receives the kickoff (when determinable)
}

export interface ScoreboardMeta {
  seasonYear: number; // e.g., 2025
  currentSeasonType: number; // 2 = regular, 3 = postseason
  currentWeek?: number; // ESPN "week" for current seasonType
  regularWeeks: number; // typically 18
  postseasonRounds: Array<{
    label: string; // Long label from API, e.g., 'Wild Card'
    shortLabel: string; // UI short, e.g., 'WC'
    value: number; // ESPN week value within seasontype=3
  }>;
}

// Map full team names to our abbreviations
const NAME_TO_ABBR: Record<string, string> = {
  'Buffalo Bills': 'BUF',
  'Miami Dolphins': 'MIA',
  'New England Patriots': 'NE',
  'New York Jets': 'NYJ',
  'Baltimore Ravens': 'BAL',
  'Cincinnati Bengals': 'CIN',
  'Cleveland Browns': 'CLE',
  'Pittsburgh Steelers': 'PIT',
  'Houston Texans': 'HOU',
  'Indianapolis Colts': 'IND',
  'Jacksonville Jaguars': 'JAX',
  'Tennessee Titans': 'TEN',
  'Denver Broncos': 'DEN',
  'Kansas City Chiefs': 'KC',
  'Las Vegas Raiders': 'LV',
  'Los Angeles Chargers': 'LAC',
  'Dallas Cowboys': 'DAL',
  'New York Giants': 'NYG',
  'Philadelphia Eagles': 'PHI',
  'Washington Commanders': 'WAS',
  'Chicago Bears': 'CHI',
  'Detroit Lions': 'DET',
  'Green Bay Packers': 'GB',
  'Minnesota Vikings': 'MIN',
  'Atlanta Falcons': 'ATL',
  'Carolina Panthers': 'CAR',
  'New Orleans Saints': 'NO',
  'Tampa Bay Buccaneers': 'TB',
  'Arizona Cardinals': 'ARI',
  'Los Angeles Rams': 'LAR',
  'San Francisco 49ers': 'SF',
  'Seattle Seahawks': 'SEA',
};

export class LiveGamesService {
  // ESPN scoreboard; CORS-friendly in practice
  private static readonly SCOREBOARD_URL = 'https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard';

  static buildUrl(params?: { seasonYear?: number; seasonType?: number; week?: number }): string {
    const u = new URL(LiveGamesService.SCOREBOARD_URL);
    if (params?.seasonYear) u.searchParams.set('dates', String(params.seasonYear));
    if (params?.seasonType) u.searchParams.set('seasontype', String(params.seasonType));
    if (params?.week) u.searchParams.set('week', String(params.week));
    return u.toString();
  }

  static async fetchScoreboardMeta(): Promise<ScoreboardMeta> {
    const resp = await fetch(LiveGamesService.SCOREBOARD_URL);
    if (!resp.ok) throw new Error(`Failed to fetch scoreboard: ${resp.status}`);
    const data = await resp.json();

    const league = (data?.leagues || [])[0] || {};
    const seasonYear = Number(league?.season?.year) || new Date().getFullYear();
    const currentSeasonType = Number(league?.season?.type?.id) || 2;
    const currentWeek = Number(data?.week?.number) || undefined;

    // Parse calendar for regular & postseason
    const calendar = Array.isArray(league?.calendar) ? league.calendar : [];
    const reg = calendar.find((c: any) => String(c?.value) === '2');
    const post = calendar.find((c: any) => String(c?.value) === '3');
    const regularWeeks = Array.isArray(reg?.entries) ? reg.entries.length : 18;

    // Build postseason round mapping; skip Pro Bowl if present
    const postseasonRounds: Array<{ label: string; shortLabel: string; value: number }> = [];
    if (Array.isArray(post?.entries)) {
      for (const e of post.entries) {
        const label: string = e?.label || '';
        const valueNum = Number(e?.value);
        if (!Number.isFinite(valueNum)) continue;
        let shortLabel: string | null = null;
        if (/Wild\s*Card/i.test(label)) shortLabel = 'WC';
        else if (/Divisional/i.test(label)) shortLabel = 'DIV';
        else if (/Conference\s*Champ/i.test(label)) shortLabel = 'CONF';
        else if (/Super\s*Bowl/i.test(label)) shortLabel = 'SB';
        // Exclude Pro Bowl and other non-playoff games
        if (shortLabel) postseasonRounds.push({ label, shortLabel, value: valueNum });
      }
      // Ensure expected order WC -> DIV -> CONF -> SB
      const order: Record<string, number> = { WC: 0, DIV: 1, CONF: 2, SB: 3 };
      postseasonRounds.sort((a, b) => (order[a.shortLabel] ?? 99) - (order[b.shortLabel] ?? 99));
    }

    return { seasonYear, currentSeasonType, currentWeek, regularWeeks, postseasonRounds };
  }

  static async fetchInProgressGames(): Promise<LiveGame[]> {
    const resp = await fetch(LiveGamesService.SCOREBOARD_URL);
    if (!resp.ok) throw new Error(`Failed to fetch scoreboard: ${resp.status}`);
    const data = await resp.json();

    const events: any[] = data?.events || [];
    const inProgress = events.filter((e) => e?.status?.type?.state === 'in' || e?.competitions?.[0]?.status?.type?.state === 'in');

    return inProgress.map((e) => {
      const comp = e.competitions?.[0];
      const status = comp?.status || e.status || {};
      const period = status?.period ?? 0;
      const clock = status?.displayClock ?? '';
      const statusText = status?.type?.shortDetail || status?.type?.detail || '';
      const statusState = status?.type?.state || '';

      const competitors = comp?.competitors || [];
      const home = competitors.find((c: any) => c.homeAway === 'home') || competitors[0];
      const away = competitors.find((c: any) => c.homeAway === 'away') || competitors[1];

      const homeName: string = home?.team?.displayName || home?.team?.name || '';
      const awayName: string = away?.team?.displayName || away?.team?.name || '';
      const homeAbbr = NAME_TO_ABBR[homeName] || (home?.team?.abbreviation || '').toUpperCase();
      const awayAbbr = NAME_TO_ABBR[awayName] || (away?.team?.abbreviation || '').toUpperCase();

      // Situation parsing
      const situation = comp?.situation || {};
      const possessionId = situation?.possession;
      const homeId = home?.team?.id;
      const awayId = away?.team?.id;
      let possessionAbbr: string | undefined;
      if (possessionId && (homeId || awayId)) {
        if (String(possessionId) === String(homeId)) possessionAbbr = homeAbbr;
        else if (String(possessionId) === String(awayId)) possessionAbbr = awayAbbr;
      }

      // Transitional/neutral state detection and last play details
      const downVal = typeof situation?.down === 'number' ? situation.down : undefined;
      const neutralPhase = (statusState === 'in') && (
        (downVal === -1) || (!situation?.downDistanceText && !situation?.possessionText)
      );
      const lp = situation?.lastPlay || {};
      const lastPlayText = lp?.text || '';
      const drive = lp?.drive || {};
      const driveResult = drive?.result || '';
      const lastDriveSummary = drive?.description || '';
      let scoringTeamAbbr: string | undefined;
      const scoringId = lp?.team?.id;
      if (scoringId) {
        if (String(scoringId) === String(homeId)) scoringTeamAbbr = homeAbbr;
        else if (String(scoringId) === String(awayId)) scoringTeamAbbr = awayAbbr;
      }
      // Derive a concise transition note and likely next receiver when possible
      let transitionNote: string | undefined;
      let nextReceivingAbbr: string | undefined;
      if (neutralPhase) {
        const scored = /TD|Touchdown/i.test(driveResult);
        const fg = /FG|Field Goal/i.test(driveResult);
        const safety = /Safety/i.test(driveResult);
        const punt = /Punt/i.test(driveResult);
        const downs = /Downs?|Turnover on Downs?/i.test(driveResult);

        if (scored || fg) {
          transitionNote = 'Kickoff pending';
          if (scoringTeamAbbr) {
            nextReceivingAbbr = scoringTeamAbbr === homeAbbr ? awayAbbr : homeAbbr;
          }
        } else if (safety) {
          transitionNote = 'Free kick pending';
          nextReceivingAbbr = scoringTeamAbbr; // team scoring the safety receives
        } else if (punt) {
          transitionNote = 'Change of possession';
        } else if (downs) {
          transitionNote = 'Turnover on downs';
        } else if (lastPlayText) {
          transitionNote = lastPlayText;
        } else {
          transitionNote = 'Break in play';
        }
      }

      return {
        id: String(e.id || comp?.id),
        homeTeamName: homeName,
        homeAbbr,
        homeScore: parseInt(home?.score ?? '0', 10),
        awayTeamName: awayName,
        awayAbbr,
        awayScore: parseInt(away?.score ?? '0', 10),
        period,
        clock,
        statusText,
        statusState,
        possessionAbbr,
        downDistanceText: situation?.downDistanceText || '',
        shortDownDistanceText: situation?.shortDownDistanceText || '',
        possessionText: situation?.possessionText || '',
        isRedZone: Boolean(situation?.isRedZone),
        yardLine: typeof situation?.yardLine === 'number' ? situation.yardLine : undefined,
        neutralPhase,
        lastPlayText,
        lastDriveSummary,
        driveResult,
        scoringTeamAbbr,
        transitionNote,
        nextReceivingAbbr,
      } as LiveGame;
    });
  }

  static async fetchThisWeekGames(): Promise<LiveGame[]> {
    const resp = await fetch(LiveGamesService.SCOREBOARD_URL);
    if (!resp.ok) throw new Error(`Failed to fetch scoreboard: ${resp.status}`);
    const data = await resp.json();

    // ESPN's unparameterized scoreboard defaults to preseason during August.
    // Weekly fantasy records only count regular-season and postseason games.
    // Missing season metadata fails closed so an unknown event cannot affect a record.
    const events: any[] = (data?.events || []).filter((event: any) => {
      const seasonType = Number(event?.season?.type);
      return seasonType === 2 || seasonType === 3;
    });

    return events.map((e) => {
      const comp = e.competitions?.[0];
      const status = comp?.status || e.status || {};
      const period = status?.period ?? 0;
      const clock = status?.displayClock ?? '';
      const statusText = status?.type?.shortDetail || status?.type?.detail || '';
      const statusState = status?.type?.state || '';

      const competitors = comp?.competitors || [];
      const home = competitors.find((c: any) => c.homeAway === 'home') || competitors[0];
      const away = competitors.find((c: any) => c.homeAway === 'away') || competitors[1];

      const homeName: string = home?.team?.displayName || home?.team?.name || '';
      const awayName: string = away?.team?.displayName || away?.team?.name || '';
      const homeAbbr = NAME_TO_ABBR[homeName] || (home?.team?.abbreviation || '').toUpperCase();
      const awayAbbr = NAME_TO_ABBR[awayName] || (away?.team?.abbreviation || '').toUpperCase();

      // Situation parsing (present when live)
      const situation = comp?.situation || {};
      const possessionId = situation?.possession;
      const homeId = home?.team?.id;
      const awayId = away?.team?.id;
      let possessionAbbr: string | undefined;
      if (possessionId && (homeId || awayId)) {
        if (String(possessionId) === String(homeId)) possessionAbbr = homeAbbr;
        else if (String(possessionId) === String(awayId)) possessionAbbr = awayAbbr;
      }

      // Transitional/neutral state detection and last play details
      const downVal = typeof situation?.down === 'number' ? situation.down : undefined;
      const neutralPhase = (statusState === 'in') && (
        (downVal === -1) || (!situation?.downDistanceText && !situation?.possessionText)
      );
      const lp = situation?.lastPlay || {};
      const lastPlayText = lp?.text || '';
      const drive = lp?.drive || {};
      const driveResult = drive?.result || '';
      const lastDriveSummary = drive?.description || '';
      let scoringTeamAbbr: string | undefined;
      const scoringId = lp?.team?.id;
      if (scoringId) {
        if (String(scoringId) === String(homeId)) scoringTeamAbbr = homeAbbr;
        else if (String(scoringId) === String(awayId)) scoringTeamAbbr = awayAbbr;
      }
      let transitionNote: string | undefined;
      let nextReceivingAbbr: string | undefined;
      if (neutralPhase) {
        const scored = /TD|Touchdown/i.test(driveResult);
        const fg = /FG|Field Goal/i.test(driveResult);
        const safety = /Safety/i.test(driveResult);
        const punt = /Punt/i.test(driveResult);
        const downs = /Downs?|Turnover on Downs?/i.test(driveResult);

        if (scored || fg) {
          transitionNote = 'Kickoff pending';
          if (scoringTeamAbbr) {
            nextReceivingAbbr = scoringTeamAbbr === homeAbbr ? awayAbbr : homeAbbr;
          }
        } else if (safety) {
          transitionNote = 'Free kick pending';
          nextReceivingAbbr = scoringTeamAbbr;
        } else if (punt) {
          transitionNote = 'Change of possession';
        } else if (downs) {
          transitionNote = 'Turnover on downs';
        } else if (lastPlayText) {
          transitionNote = lastPlayText;
        } else {
          transitionNote = 'Break in play';
        }
      }

      return {
        id: String(e.id || comp?.id),
        homeTeamName: homeName,
        homeAbbr,
        homeScore: parseInt(home?.score ?? '0', 10),
        awayTeamName: awayName,
        awayAbbr,
        awayScore: parseInt(away?.score ?? '0', 10),
        period,
        clock,
        statusText,
        statusState,
        possessionAbbr,
        downDistanceText: situation?.downDistanceText || '',
        shortDownDistanceText: situation?.shortDownDistanceText || '',
        possessionText: situation?.possessionText || '',
        isRedZone: Boolean(situation?.isRedZone),
        yardLine: typeof situation?.yardLine === 'number' ? situation.yardLine : undefined,
        neutralPhase,
        lastPlayText,
        lastDriveSummary,
        driveResult,
        scoringTeamAbbr,
        transitionNote,
        nextReceivingAbbr,
      } as LiveGame;
    });
  }

  static async fetchGamesFor(params: { seasonYear: number; seasonType: number; week?: number }): Promise<LiveGame[]> {
    const url = LiveGamesService.buildUrl({ seasonYear: params.seasonYear, seasonType: params.seasonType, week: params.week });
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Failed to fetch scoreboard: ${resp.status}`);
    const data = await resp.json();

    const events: any[] = data?.events || [];

    return events.map((e) => {
      const comp = e.competitions?.[0];
      const status = comp?.status || e.status || {};
      const period = status?.period ?? 0;
      const clock = status?.displayClock ?? '';
      const statusText = status?.type?.shortDetail || status?.type?.detail || '';
      const statusState = status?.type?.state || '';

      const competitors = comp?.competitors || [];
      const home = competitors.find((c: any) => c.homeAway === 'home') || competitors[0];
      const away = competitors.find((c: any) => c.homeAway === 'away') || competitors[1];

      const homeName: string = home?.team?.displayName || home?.team?.name || '';
      const awayName: string = away?.team?.displayName || away?.team?.name || '';
      const homeAbbr = NAME_TO_ABBR[homeName] || (home?.team?.abbreviation || '').toUpperCase();
      const awayAbbr = NAME_TO_ABBR[awayName] || (away?.team?.abbreviation || '').toUpperCase();

      const situation = comp?.situation || {};
      const possessionId = situation?.possession;
      const homeId = home?.team?.id;
      const awayId = away?.team?.id;
      let possessionAbbr: string | undefined;
      if (possessionId && (homeId || awayId)) {
        if (String(possessionId) === String(homeId)) possessionAbbr = homeAbbr;
        else if (String(possessionId) === String(awayId)) possessionAbbr = awayAbbr;
      }

      const downVal = typeof situation?.down === 'number' ? situation.down : undefined;
      const neutralPhase = (statusState === 'in') && (
        (downVal === -1) || (!situation?.downDistanceText && !situation?.possessionText)
      );
      const lp = situation?.lastPlay || {};
      const lastPlayText = lp?.text || '';
      const drive = lp?.drive || {};
      const driveResult = drive?.result || '';
      const lastDriveSummary = drive?.description || '';
      let scoringTeamAbbr: string | undefined;
      const scoringId = lp?.team?.id;
      if (scoringId) {
        if (String(scoringId) === String(homeId)) scoringTeamAbbr = homeAbbr;
        else if (String(scoringId) === String(awayId)) scoringTeamAbbr = awayAbbr;
      }
      let transitionNote: string | undefined;
      let nextReceivingAbbr: string | undefined;
      if (neutralPhase) {
        const scored = /TD|Touchdown/i.test(driveResult);
        const fg = /FG|Field Goal/i.test(driveResult);
        const safety = /Safety/i.test(driveResult);
        const punt = /Punt/i.test(driveResult);
        const downs = /Downs?|Turnover on Downs?/i.test(driveResult);

        if (scored || fg) {
          transitionNote = 'Kickoff pending';
          if (scoringTeamAbbr) {
            nextReceivingAbbr = scoringTeamAbbr === homeAbbr ? awayAbbr : homeAbbr;
          }
        } else if (safety) {
          transitionNote = 'Free kick pending';
          nextReceivingAbbr = scoringTeamAbbr;
        } else if (punt) {
          transitionNote = 'Change of possession';
        } else if (downs) {
          transitionNote = 'Turnover on downs';
        } else if (lastPlayText) {
          transitionNote = lastPlayText;
        } else {
          transitionNote = 'Break in play';
        }
      }

      return {
        id: String(e.id || comp?.id),
        homeTeamName: homeName,
        homeAbbr,
        homeScore: parseInt(home?.score ?? '0', 10),
        awayTeamName: awayName,
        awayAbbr,
        awayScore: parseInt(away?.score ?? '0', 10),
        period,
        clock,
        statusText,
        statusState,
        possessionAbbr,
        downDistanceText: situation?.downDistanceText || '',
        shortDownDistanceText: situation?.shortDownDistanceText || '',
        possessionText: situation?.possessionText || '',
        isRedZone: Boolean(situation?.isRedZone),
        yardLine: typeof situation?.yardLine === 'number' ? situation.yardLine : undefined,
        neutralPhase,
        lastPlayText,
        lastDriveSummary,
        driveResult,
        scoringTeamAbbr,
        transitionNote,
        nextReceivingAbbr,
      } as LiveGame;
    });
  }
}
